// src/admin_panel_page.js
import { useState, useEffect } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './config/firebase';

const SECTIONS = [
  { id: 'nav', title: 'Navigasyon', keys: ['navHome', 'navAbout', 'navExp', 'navVol', 'navProj', 'navCert', 'navContact'] },
  { id: 'header', title: 'Üst Bilgi', keys: ['headerTitle', 'profileImg'] },
  { id: 'about', title: 'Hakkımda', keys: ['aboutTitle', 'aboutText', 'eduTitle', 'uni1', 'dept', 'uni2', 'gpa', 'skillsTitle', 'skillsList', 'langTitle', 'langList'] },
  { id: 'experience', title: 'Deneyim', keys: ['expTitle', 'jobs'] },
  { id: 'internships', title: 'Stajlar', keys: ['internships'] },
  { id: 'volunteering', title: 'Gönüllülük', keys: ['volTitle', 'volJobs'] },
  { id: 'projects', title: 'Projeler', keys: ['projTitle', 'projects'] },
  { id: 'certificates', title: 'Sertifikalar', keys: ['certTitle', 'certificates'] },
  { id: 'contact', title: 'İletişim', keys: ['contactTitle', 'copyright', 'modalTitle', 'formName', 'formEmail', 'formMsg', 'formBtn', 'phName', 'phEmail', 'phMsg'] }
];

const getTemplateForKey = (key) => {
  switch (key) {
    case 'jobs':
      return { title: '', details: '', desc: '', icon: '' };
    case 'internships':
      return { title: '', details: '', desc: '', icon: '' };
    case 'volJobs':
      return { title: '', details: '', desc: '', icon: '' };
    case 'projects':
      return { id: Date.now(), title: '', description: '', githubLink: '', icon: '' };
    case 'certificates':
      return { id: Date.now(), title: '', issuer: '', date: '', skills: '', desc: '', image: '', icon: '' };
    default:
      return { title: '', description: '', icon: '' };
  }
};

export default function AdminPanel({ content, setContent, defaultContent, darkMode }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false); 
  const [editData, setEditData] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [loginError, setLoginError] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const [certificateDraft, setCertificateDraft] = useState({
    title: '', issuer: '', date: '', skills: '', desc: '', image: '', icon: ''
  });

  // Gizli Admin Girişi için Klavye Kısayolu
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'y') {
        if (!isAdmin && !showLoginModal) {
          e.preventDefault(); // Başka bir uygulamanın/tarayıcının kısayolunu ez
          setShowLoginModal(true);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAdmin, showLoginModal]);

  // Oturum durumunu dinle (Sayfa yenilendiğinde admin girişi açık kalır)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Bildirim Gösterme
  const showToast = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Onay İsteme
  const requestConfirm = (message, onConfirm) => {
    setConfirmModal({ message, onConfirm });
  };

  // Admin Giriş Fonksiyonu
  const handleAdminLogin = () => {
    setShowLoginModal(true);
  };

  // Şifre Kontrolü
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await signInWithEmailAndPassword(auth, emailInput, passwordInput);
      setIsAdmin(true);
      setShowLoginModal(false);
      setEmailInput("");
      setPasswordInput("");
      setLoginError(false);
      openAdminPanel();
      showToast("Başarıyla giriş yapıldı!", "success");
    } catch (error) {
      console.error("Giriş hatası:", error);
      setLoginError(true);
      showToast("E-posta veya şifre hatalı!", "error");
    } finally {
      setLoginLoading(false);
    }
  };

  // Admin Panelini Aç
  const openAdminPanel = () => {
    setEditData(JSON.parse(JSON.stringify(content))); 
    setActiveSection(SECTIONS[0].id); // Menü açıldığında ilk sekmeyi aktif yap
    setShowAdminModal(true);
  };

  const handleAddCertificateFromModal = () => {
    const nextItem = {
      id: Date.now(),
      title: certificateDraft.title || 'Yeni Sertifika',
      issuer: certificateDraft.issuer || 'Kuruluş Adı',
      date: certificateDraft.date || 'Tarih',
      skills: certificateDraft.skills || '',
      desc: certificateDraft.desc || '',
      image: certificateDraft.image || '',
      icon: certificateDraft.icon || ''
    };

    setEditData({
      ...editData,
      certificates: [nextItem, ...(editData.certificates || [])]
    });

    setCertificateDraft({ title: '', issuer: '', date: '', skills: '', desc: '', image: '', icon: '' });
    setShowCertificateModal(false);
    showToast('Sertifika eklendi.', 'success');
  };

  // Değişiklikleri Kaydet
  const handleSaveContent = async () => {
    try {
      setContent(editData);
      await setDoc(doc(db, 'portfolio', 'content'), editData);
      showToast("Değişiklikler başarıyla kaydedildi!", "success");
      setShowAdminModal(false);
    } catch (error) {
      showToast("Hata oluştu: " + error.message, "error");
    }
  };

  // Varsayılanlara Dön
  const handleResetContent = () => {
    requestConfirm("Tüm değişiklikleri silip varsayılan ayarlara dönmek istediğinize emin misiniz?", async () => {
      setContent(defaultContent);
      await setDoc(doc(db, 'portfolio', 'content'), defaultContent);
      setEditData(JSON.parse(JSON.stringify(defaultContent)));
      setShowAdminModal(false);
      showToast("Varsayılan ayarlara dönüldü.", "success");
    });
  };

  // Değişiklikleri Geri Al (Kaydedilmemiş değişiklikleri iptal et)
  const handleUndoChanges = () => {
    requestConfirm("Kaydedilmemiş değişiklikleri geri almak istediğinize emin misiniz?", () => {
      setEditData(JSON.parse(JSON.stringify(content)));
      showToast("Değişiklikler geri alındı.", "success");
    });
  };

  // Kapatma işlemi (Değişiklik kontrolü)
  const handleClose = () => {
    const hasChanges = JSON.stringify(content) !== JSON.stringify(editData);
    if (hasChanges) {
      requestConfirm("Kaydedilmemiş değişiklikler var. Çıkmak istediğinize emin misiniz?", () => {
        setShowAdminModal(false);
      });
    } else {
      setShowAdminModal(false);
    }
  };

  // Çıkış Yapma Fonksiyonu
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsAdmin(false);
      setShowAdminModal(false);
      showToast("Çıkış yapıldı.", "success");
    } catch (error) {
      showToast("Çıkış yapılamadı.", "error");
    }
  };

  // Resim dosyasını Base64 formatına çevir
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const MAX_WIDTH = 1024; // Genişliği sınırla
          
          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Sıkıştırarak LocalStorage sınırına takılmayı önle
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = error => reject(error);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Resim yükleme işlemi
  const handleImageChange = async (e, key, index = null, subKey = null) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const base64 = await convertToBase64(file);

      if (index !== null && subKey !== null) {
        // Liste içindeki obje güncellemesi (Örn: Projeler)
        const newList = [...editData[key]];
        newList[index] = { ...newList[index], [subKey]: base64 };
        
        setEditData({ ...editData, [key]: newList });
      } else {
        // Düz alan güncellemesi
        setEditData({ ...editData, [key]: base64 });
      }
    } catch (error) {
      console.error("Resim dönüştürme hatası:", error);
      showToast("Resim yüklenirken bir hata oluştu.", "error");
    }
  };

  // Tek bir alan için input oluşturma
  const renderField = (key, value) => {
    if (value === undefined) return null;
    
      // 1. Düz Metinler (String)
      if (typeof value === 'string') {
        const isLongText = value.length > 50 || key.includes('Text') || key.includes('desc');
        const isImage = /icon|logo|img|image|src|url|photo|pic/i.test(key);

        return (
          <div key={key} className="admin-form-group">
            <label>{key}</label>
            {isLongText ? (
              <textarea
                value={value}
                onChange={(e) => setEditData({ ...editData, [key]: e.target.value })}
                className="form-textarea"
                rows={3}
              />
            ) : (
              <input
                type="text"
                value={value}
                onChange={(e) => setEditData({ ...editData, [key]: e.target.value })}
                className="form-input"
              />
            )}
            {isImage && (
              <div className="admin-image-upload">
                {value && (
                  <div className="admin-image-preview">
                    <img src={value} alt={key} />
                  </div>
                )}
                <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, key)} className="admin-file-input" />
                <small>Bilgisayarınızdan yeni bir resim seçebilirsiniz.</small>
              </div>
            )}
          </div>
        );
      } 
      // 2. Listeler (Array)
      else if (Array.isArray(value)) {
        // 2a. Metin Listeleri (Örn: Yetenekler, Diller)
        if ((value.length > 0 && typeof value[0] === 'string') || (value.length === 0 && ['skillsList', 'langList'].includes(key))) {
          return (
            <div key={key} className="admin-form-group">
              <label>{key}</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {value.map((item, index) => (
                  <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => {
                        const newList = [...value];
                        newList[index] = e.target.value;
                        setEditData({ ...editData, [key]: newList });
                      }}
                      className="form-input"
                      style={{ flex: 1, padding: '10px 14px' }}
                    />
                    <button 
                      className="delete-btn"
                      onClick={() => {
                        const newList = value.filter((_, i) => i !== index);
                        setEditData({ ...editData, [key]: newList });
                      }}
                    >
                      Sil
                    </button>
                  </div>
                ))}
                <button 
                  className="add-btn"
                  onClick={() => {
                    setEditData({ ...editData, [key]: [...value, ""] });
                  }}
                >
                  + Yeni Ekle
                </button>
              </div>
            </div>
          );
        } 
        // 2b. Obje Listeleri (Örn: Deneyimler, Projeler)
        else if (value.length === 0 || (value.length > 0 && typeof value[0] === 'object')) {
          const canHaveObjects = ['jobs', 'internships', 'volJobs', 'projects', 'certificates'].includes(key);

          return (
            <div key={key} className="admin-form-group-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px', gap: '10px' }}>
                <h3 style={{ margin: 0 }}>{key}</h3>
                <div className="admin-segmented-actions">
                  {key === 'certificates' ? (
                    <button className="add-btn primary" style={{ margin: 0 }} onClick={() => setShowCertificateModal(true)}>
                      + Yeni Sertifika Ekle
                    </button>
                  ) : canHaveObjects ? (
                    <button className="add-btn primary" style={{ margin: 0 }} onClick={() => {
                      const template = getTemplateForKey(key);
                      if (template.id) template.id = Date.now();
                      setEditData({ ...editData, [key]: value.length ? [template, ...value] : [template] });
                    }}>
                      {key === 'jobs' ? '+ Yeni Deneyim Ekle' : key === 'internships' ? '+ Yeni Staj Ekle' : key === 'volJobs' ? '+ Yeni Gönüllülük Ekle' : '+ Yeni Ekle'}
                    </button>
                  ) : null}
                </div>
              </div>
              {value.map((item, index) => (
                <div key={index} className="admin-sub-group">
                  <div className="admin-item-header">
                    <h4>{key === 'certificates' ? `Sertifika #${index + 1}` : key === 'jobs' ? `Deneyim #${index + 1}` : key === 'internships' ? `Staj #${index + 1}` : key === 'volJobs' ? `Gönüllülük #${index + 1}` : `Öğe #${index + 1}`}</h4>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button 
                        className="move-btn"
                        disabled={index === 0}
                        onClick={() => {
                          const newList = [...value];
                          [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
                          
                          setEditData({ ...editData, [key]: newList });
                        }}
                        title="Yukarı Taşı"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>
                      </button>
                      <button 
                        className="move-btn"
                        disabled={index === value.length - 1}
                        onClick={() => {
                          const newList = [...value];
                          [newList[index + 1], newList[index]] = [newList[index], newList[index + 1]];
                           
                          setEditData({ ...editData, [key]: newList });
                        }}
                        title="Aşağı Taşı"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                      </button>
                      <button className="delete-btn" onClick={() => {
                          const newList = value.filter((_, i) => i !== index);
                          
                          setEditData({ ...editData, [key]: newList });
                      }}>Sil</button>
                    </div>
                  </div>
                  <div className="admin-item-form-grid">
                    {Object.keys(item).map((subKey) => {
                      const isSubImage = /icon|logo|img|image|src|url|photo|pic/i.test(subKey);
                      const isTextArea = subKey === 'desc' || subKey === 'description' || subKey === 'skills' || (typeof item[subKey] === 'string' && item[subKey].length > 50);

                      return (
                        <div key={subKey} className="admin-form-group">
                          <label>{subKey}</label>
                          {isTextArea ? (
                            <textarea
                              value={item[subKey]}
                              onChange={(e) => {
                                const newList = [...value];
                                newList[index] = { ...newList[index], [subKey]: e.target.value };
                                setEditData({ ...editData, [key]: newList });
                              }}
                              className="form-textarea"
                              rows={subKey === 'description' || subKey === 'desc' ? 4 : 3}
                            />
                          ) : (
                            <input
                              type="text"
                              value={item[subKey]}
                              onChange={(e) => {
                                const newList = [...value];
                                newList[index] = { ...newList[index], [subKey]: e.target.value };
                                setEditData({ ...editData, [key]: newList });
                              }}
                              className="form-input"
                            />
                          )}
                          {isSubImage && (
                            <div className="admin-image-upload">
                              {item[subKey] && (
                                <div className="admin-image-preview">
                                  <img src={item[subKey]} alt={subKey} />
                                </div>
                              )}
                              <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, key, index, subKey)} className="admin-file-input" />
                              <small>{subKey === 'icon' ? 'İkon / logo görseli yükleyin.' : 'Resim önizlemesini kontrol edin.'}</small>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        }
      }
  };

  const adminStyles = `
    .admin-style-wrapper {
      --primary: #d39e00;
      --primary-hover: #b38600;
      --danger: #ef4444;
      --danger-hover: #dc2626;
      --success: #10b981;
      --surface: #ffffff;
      --background: #f8fafc;
      --border: rgba(211, 158, 0, 0.2);
      --text-main: #0f172a;
      --text-muted: #64748b;
      --radius: 16px;
      --shadow: 0 20px 40px -8px rgba(0,0,0,0.15);
    }
    .admin-style-wrapper.dark-admin {
      --primary: #ffc107;
      --primary-hover: #ffca28;
      --surface: #121212;
      --background: #050505;
      --border: rgba(255, 193, 7, 0.15);
      --text-main: #f3f4f6;
      --text-muted: #9ca3af;
      --shadow: 0 25px 50px -12px rgba(0,0,0,0.8);
    }

    /* Base Modals */
    .admin-modal-overlay {
      position: fixed; inset: 0; background: rgba(0,0,0,0.4);
      backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); z-index: 9999;
      display: flex; align-items: center; justify-content: center;
      animation: adminFadeIn 0.2s ease-out;
    }
    @keyframes adminFadeIn { from { opacity: 0; } to { opacity: 1; } }
    
    .admin-modal {
      background: var(--surface); color: var(--text-main);
      width: 100vw; max-width: none; height: 100vh; max-height: none;
      border-radius: 0; box-shadow: none;
      display: flex; flex-direction: column; overflow: hidden;
      font-family: system-ui, -apple-system, sans-serif;
    }

    /* Header */
    .admin-header {
      display: flex; justify-content: space-between; align-items: center;
      padding: 20px 32px; border-bottom: 1px solid var(--border);
      background: var(--surface);
    }
    .admin-header-left { display: flex; align-items: center; gap: 16px; }
    .admin-header-left h2 { margin: 0; font-size: 1.4rem; font-weight: 700; color: var(--primary); letter-spacing: -0.5px;}
    .admin-sidebar-toggle {
      background: var(--background); border: 1px solid var(--border); font-size: 1.2rem; color: var(--text-main);
      cursor: pointer; display: none; padding: 6px 12px; border-radius: 8px; transition: 0.2s;
    }
    .admin-sidebar-toggle:hover { filter: brightness(0.95); }
    @media (max-width: 768px) { .admin-sidebar-toggle { display: block; } }

    .admin-header-right { display: flex; align-items: center; gap: 20px; }
    .lang-selector { display: flex; align-items: center; gap: 10px; font-size: 0.95rem; font-weight: 500; color: var(--text-muted); }
    .lang-selector select {
      padding: 8px 16px; border-radius: 8px; border: 1px solid var(--border); font-weight: 600;
      background: var(--background); color: var(--text-main); outline: none; cursor: pointer; transition: 0.2s;
    }
    .lang-selector select:focus { border-color: var(--primary); }
    
    .btn-close-icon {
      background: transparent; border: none; font-size: 2rem; color: var(--text-muted);
      cursor: pointer; line-height: 1; padding: 0 8px; border-radius: 8px; transition: all 0.3s ease;
      display: flex; align-items: center; justify-content: center; height: 40px; width: 40px;
    }
    .btn-close-icon:hover { color: var(--danger); background: rgba(239,68,68,0.1); transform: rotate(90deg); }

    /* Body & Sidebar */
    .admin-body { display: flex; flex: 1; overflow: hidden; background: var(--background); }
    .admin-sidebar {
      width: 260px; background: var(--surface); border-right: 1px solid var(--border);
      display: flex; flex-direction: column; overflow-y: auto; padding: 24px 16px; transition: transform 0.3s ease;
    }
    .admin-sidebar-link {
      padding: 12px 16px; color: var(--text-muted); text-decoration: none; font-weight: 600; font-size: 0.95rem;
      border-radius: 12px; transition: all 0.2s ease; margin-bottom: 8px;
      display: flex; align-items: center;
    }
    .admin-sidebar-link:hover { background: var(--background); color: var(--primary); transform: translateX(4px); }
    .admin-sidebar-link.active { background: var(--background); color: var(--primary); transform: translateX(4px); box-shadow: inset 4px 0 0 var(--primary); }
    
    /* Custom Scrollbar */
    .admin-sidebar::-webkit-scrollbar, .admin-content-area::-webkit-scrollbar {
      width: 8px;
    }
    .admin-sidebar::-webkit-scrollbar-track, .admin-content-area::-webkit-scrollbar-track {
      background: transparent;
    }
    .admin-sidebar::-webkit-scrollbar-thumb {
      background-color: var(--border);
      border-radius: 20px;
      border: 2px solid var(--surface);
    }
    .admin-content-area::-webkit-scrollbar-thumb {
      background-color: var(--border);
      border-radius: 20px;
      border: 2px solid var(--background);
    }
    .admin-sidebar::-webkit-scrollbar-thumb:hover, .admin-content-area::-webkit-scrollbar-thumb:hover {
      background-color: var(--text-muted);
    }
    
    /* Content Area */
    .admin-content-area { flex: 1; padding: 32px; overflow-y: auto; scroll-behavior: smooth; }
    .admin-section-container {
      background: var(--surface); border-radius: 12px; padding: 32px;
      margin-bottom: 32px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border: 1px solid var(--border);
    }
    .admin-section-title { margin-top: 0; border-bottom: 2px solid var(--background); padding-bottom: 16px; color: var(--text-main); font-size: 1.3rem; margin-bottom: 24px;}

    /* Forms */
    .admin-form-group { margin-bottom: 20px; text-align: left; }
    .admin-form-group label { display: block; margin-bottom: 8px; font-weight: 600; color: var(--text-muted); font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; }
    .form-input, .form-textarea {
      width: 100%; padding: 12px 16px; border-radius: 8px; border: 1px solid var(--border);
      background: var(--background); color: var(--text-main); transition: all 0.2s; box-sizing: border-box; font-family: inherit; font-size: 0.95rem;
    }
    .form-input:focus, .form-textarea:focus { border-color: var(--primary); box-shadow: 0 0 0 4px rgba(211,158,0,0.15); outline: none; background: var(--surface); }

    /* Sub Groups (Lists) */
    .admin-sub-group {
      border: 1px solid var(--border); border-radius: 12px; padding: 20px; margin-bottom: 20px; background: var(--background); transition: 0.2s;
    }
    .admin-sub-group:hover { border-color: #cbd5e1; }
    .admin-item-header {
      display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 14px;
    }
    .admin-item-header h4 { margin: 0; color: var(--text-main); font-size: 1.1rem; }
    .admin-item-form-grid {
      display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 14px 16px;
    }
    .admin-item-form-grid .admin-form-group {
      margin-bottom: 0;
    }
    
    /* Image Upload */
    .admin-image-upload { display: flex; flex-direction: column; gap: 12px; margin-top: 12px; }
    .admin-image-upload small { color: var(--text-muted); }
    .admin-image-preview {
      width: 100%; min-height: 180px; display: flex; align-items: center; justify-content: center; padding: 14px; border: 2px solid var(--border); border-radius: 12px; background: rgba(255,255,255,0.02);
    }
    .admin-image-preview img { max-width: 100%; max-height: 220px; object-fit: contain; border-radius: 8px; border: 2px solid var(--border); box-shadow: 0 4px 6px rgba(0,0,0,0.05); background: rgba(255,255,255,0.04); }

    /* Buttons */
    button { font-family: inherit; }
    .btn-save, .btn-undo, .btn-reset, .btn-close {
      padding: 12px 24px; border-radius: 8px; font-weight: 600; border: none; cursor: pointer; transition: all 0.2s; font-size: 0.95rem;
    }
    .btn-save { background: var(--primary); color: #000; box-shadow: 0 4px 12px rgba(211,158,0,0.3); }
    .btn-save:hover { background: var(--primary-hover); transform: translateY(-1px); }
    .btn-undo { background: var(--surface); color: var(--text-main); border: 1px solid var(--border); }
    .btn-undo:hover { background: var(--background); border-color: #94a3b8;}
    .btn-reset { background: rgba(239, 68, 68, 0.1); color: var(--danger); }
    .btn-reset:hover { background: var(--danger); color: white; }
    .btn-close { background: var(--text-main); color: var(--surface); }
    .btn-close:hover { opacity: 0.9; }
    .admin-modal-footer {
      padding: 20px 32px; border-top: 1px solid var(--border); display: flex; gap: 16px; justify-content: flex-end; background: var(--surface); align-items: center;
    }
    
    .move-btn, .delete-btn, .add-btn {
      padding: 8px 14px; border-radius: 6px; font-size: 0.85rem; font-weight: 600; cursor: pointer; border: none; transition: 0.2s; display: inline-flex; align-items: center; gap: 6px;
    }
    .move-btn { background: var(--surface); color: var(--text-main); border: 1px solid var(--border); }
    .move-btn:hover:not(:disabled) { background: var(--background); border-color: #94a3b8; }
    .move-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .delete-btn { background: rgba(239,68,68,0.1); color: var(--danger); }
    .delete-btn:hover { background: var(--danger); color: white; }
    .admin-segmented-actions {
      display: flex; justify-content: flex-end; align-items: center; gap: 8px; flex-wrap: wrap;
    }
    .add-btn { background: rgba(16,185,129,0.1); color: var(--success); font-size: 0.85rem; padding: 8px 14px; white-space: nowrap; width: max-content; flex-shrink: 0; border-radius: 10px; }
    .add-btn.primary {
      background: linear-gradient(135deg, rgba(211,158,0,0.18), rgba(16,185,129,0.12));
      color: var(--primary);
      border: 1px solid rgba(211,158,0,0.35);
    }
    .add-btn:hover { background: var(--success); color: white; }
    .add-btn.primary:hover { background: var(--primary); color: #000; }

    /* Modals */
    .login-modal-content, .confirm-modal-content {
      background: var(--surface); color: var(--text-main); padding: 40px; border-radius: var(--radius);
      width: 90%; max-width: 420px; box-shadow: var(--shadow); position: relative;
    }
    .login-modal-content h3, .confirm-modal-content h3 { margin-top: 0; font-size: 1.5rem; text-align: center; color: var(--text-main); font-weight: 700; margin-bottom: 24px; }
    .form-submit-btn { width: 100%; padding: 14px; background: var(--primary); color: #000; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; margin-top: 24px; transition: 0.2s; font-size: 1rem; box-shadow: 0 4px 12px rgba(211,158,0,0.3);}
    .form-submit-btn:hover { background: var(--primary-hover); transform: translateY(-1px);}
    .input-error { border-color: var(--danger) !important; box-shadow: 0 0 0 3px rgba(239,68,68,0.15) !important; }

    /* Confirm Buttons */
    .confirm-buttons { display: flex; gap: 16px; justify-content: center; margin-top: 32px; }
    .btn-confirm-yes { background: var(--primary); color: #000; padding: 12px 32px; border: none; border-radius: 8px; cursor: pointer; font-weight: 600; transition: 0.2s;}
    .btn-confirm-yes:hover { background: var(--primary-hover); }
    .btn-confirm-no { background: var(--surface); color: var(--text-main); border: 1px solid var(--border); padding: 12px 32px; border-radius: 8px; cursor: pointer; font-weight: 600; transition: 0.2s;}
    .btn-confirm-no:hover { background: var(--background); }

    /* Toast */
    .admin-toast {
      position: fixed; top: 24px; right: 24px; padding: 16px 24px; border-radius: 8px;
      color: white; font-weight: 600; box-shadow: 0 10px 25px rgba(0,0,0,0.2); z-index: 10001;
      animation: toastSlide 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); display: flex; align-items: center; gap: 12px;
    }
    .admin-toast.success { background: var(--success); }
    .admin-toast.error { background: var(--danger); }
    @keyframes toastSlide { from { transform: translateX(120%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }

    /* FAB Buttons */
    .admin-fab-container { position: fixed; bottom: 30px; left: 30px; display: flex; flex-direction: column; gap: 12px; z-index: 9000; }
    .admin-fab {
      background: var(--surface); color: var(--text-main); padding: 14px 24px; border-radius: 30px;
      box-shadow: 0 8px 20px rgba(0,0,0,0.15); border: 1px solid var(--border); font-weight: 600;
      cursor: pointer; display: flex; align-items: center; gap: 10px; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); font-size: 0.95rem;
    }
    .admin-fab:hover { box-shadow: 0 12px 28px rgba(0,0,0,0.2); transform: translateY(-3px) scale(1.02); color: var(--primary); border-color: var(--primary); }
    .admin-fab.logout-fab:hover { color: var(--danger); border-color: var(--danger); }

    /* Dark Mode Specific Enhancements */
    .dark-admin .admin-modal, .dark-admin .login-modal-content, .dark-admin .confirm-modal-content {
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    .dark-admin .form-input, .dark-admin .form-textarea {
      background: rgba(255, 255, 255, 0.03);
    }
    .dark-admin .form-input:focus, .dark-admin .form-textarea:focus {
      background: rgba(0, 0, 0, 0.4);
      box-shadow: 0 0 0 4px rgba(255, 193, 7, 0.2);
    }
    .dark-admin .admin-sub-group {
      background: rgba(255, 255, 255, 0.02);
    }
    .dark-admin .admin-sub-group:hover {
      background: rgba(255, 255, 255, 0.04);
      border-color: rgba(255, 255, 255, 0.15);
    }
    .dark-admin .admin-sidebar-link.active {
      background: linear-gradient(90deg, rgba(255, 193, 7, 0.15) 0%, transparent 100%);
    }
    .dark-admin .btn-undo, .dark-admin .move-btn, .dark-admin .btn-confirm-no {
      background: rgba(255, 255, 255, 0.05);
    }
    .dark-admin .btn-undo:hover, .dark-admin .move-btn:hover:not(:disabled), .dark-admin .btn-confirm-no:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.2);
    }
    .dark-admin .lang-selector select {
      background: rgba(255, 255, 255, 0.05);
    }

    /* Mobile Sidebar */
    @media (max-width: 768px) {
      .admin-sidebar { position: fixed; top: 0; bottom: 0; left: -100%; z-index: 1000; box-shadow: var(--shadow); width: 80%; max-width: 300px;}
      .admin-sidebar.open { left: 0; }
      .admin-sidebar-overlay {
        position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 999; backdrop-filter: blur(4px);
        opacity: 0; visibility: hidden; transition: 0.3s;
      }
      .admin-sidebar-overlay.open { opacity: 1; visibility: visible; }
      .admin-header { padding: 16px 20px; }
      .admin-content-area { padding: 20px; }
      .admin-section-container { padding: 20px; }
      .admin-modal-footer { flex-direction: column; padding: 20px; }
      .admin-modal-footer button { width: 100%; }
    }
  `;

  return (
    <div className={`admin-style-wrapper ${darkMode ? 'dark-admin' : ''}`}>
      <style>{adminStyles}</style>

      {/* Bildirim Toast */}
      {notification && (
        <div className={`admin-toast ${notification.type}`}>
          {notification.type === 'success' ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          )}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Onay Modalı */}
      {confirmModal && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal-content">
            <h3>Onay Gerekiyor</h3>
            <p>{confirmModal.message}</p>
            <div className="confirm-buttons">
              <button className="btn-confirm-yes" onClick={() => {
                confirmModal.onConfirm();
                setConfirmModal(null);
              }}>Evet</button>
              <button className="btn-confirm-no" onClick={() => setConfirmModal(null)}>Hayır</button>
            </div>
          </div>
        </div>
      )}

      {/* Login Modalı */}
      {showLoginModal && (
        <div className="admin-modal-overlay">
          <div className="login-modal-content">
            <button className="btn-close-icon" style={{position: 'absolute', top: '15px', right: '15px'}} onClick={() => {
                setShowLoginModal(false);
                setEmailInput("");
                setPasswordInput("");
                setLoginError(false);
            }}>
              &times;
            </button>
            <h3 style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'10px'}}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15.5 7.5 2.3 2.3a1 1 0 0 0 1.4 0l2.1-2.1a1 1 0 0 0 0-1.4L19 4"/><path d="m21 2-9.6 9.6"/><circle cx="7.5" cy="15.5" r="5.5"/></svg>
              Admin Girişi
            </h3>
            <form onSubmit={handleLoginSubmit}>
                <div className="admin-form-group">
                    <input 
                        type="email" 
                        placeholder="E-posta adresi"
                        className={`form-input ${loginError ? 'input-error' : ''}`} 
                        value={emailInput} 
                        onChange={(e) => {
                          setEmailInput(e.target.value);
                          setLoginError(false);
                        }}
                        style={{width: '100%', boxSizing: 'border-box', marginBottom: '10px'}}
                        autoFocus
                    />
                    <input 
                        type="password" 
                        placeholder="Şifre"
                        className={`form-input ${loginError ? 'input-error' : ''}`} 
                        value={passwordInput} 
                        onChange={(e) => {
                          setPasswordInput(e.target.value);
                          setLoginError(false);
                        }}
                        style={{width: '100%', boxSizing: 'border-box'}}
                    />
                </div>
                <button type="submit" className="form-submit-btn" disabled={loginLoading}>
                  {loginLoading ? "Giriş Yapılıyor..." : "Giriş Yap"}
                </button>
            </form>
          </div>
        </div>
      )}

      {showCertificateModal && (
        <div className="admin-modal-overlay" style={{ zIndex: 10000 }} onClick={() => setShowCertificateModal(false)}>
          <div className="admin-modal" style={{ maxWidth: 780, width: '92vw', height: 'auto', maxHeight: '85vh', borderRadius: 18, margin: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="admin-header" style={{ padding: '22px 24px' }}>
              <div className="admin-header-left">
                <h2>Yeni Sertifika</h2>
              </div>
              <button className="btn-close-icon" onClick={() => setShowCertificateModal(false)}>&times;</button>
            </div>
            <div className="admin-content-area" style={{ maxHeight: '65vh', padding: '24px' }}>
              <div className="admin-item-form-grid">
                <div className="admin-form-group">
                  <label>title</label>
                  <input className="form-input" value={certificateDraft.title} onChange={(e) => setCertificateDraft({ ...certificateDraft, title: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label>issuer</label>
                  <input className="form-input" value={certificateDraft.issuer} onChange={(e) => setCertificateDraft({ ...certificateDraft, issuer: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label>date</label>
                  <input className="form-input" value={certificateDraft.date} onChange={(e) => setCertificateDraft({ ...certificateDraft, date: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label>skills</label>
                  <input className="form-input" value={certificateDraft.skills} onChange={(e) => setCertificateDraft({ ...certificateDraft, skills: e.target.value })} />
                </div>
                <div className="admin-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>desc</label>
                  <textarea className="form-textarea" rows={3} value={certificateDraft.desc} onChange={(e) => setCertificateDraft({ ...certificateDraft, desc: e.target.value })} />
                </div>
                <div className="admin-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>icon</label>
                  <input className="form-input" value={certificateDraft.icon} onChange={(e) => setCertificateDraft({ ...certificateDraft, icon: e.target.value })} placeholder="/ikonlar/logo.jpg" />
                  {certificateDraft.icon && (
                    <div className="admin-image-upload">
                      <div className="admin-image-preview">
                        <img src={certificateDraft.icon} alt="Certificate icon preview" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="admin-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>image</label>
                  <input className="form-input" value={certificateDraft.image} onChange={(e) => setCertificateDraft({ ...certificateDraft, image: e.target.value })} placeholder="/images/certificate.jpg" />
                  {certificateDraft.image && (
                    <div className="admin-image-upload">
                      <div className="admin-image-preview">
                        <img src={certificateDraft.image} alt="Certificate preview" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="btn-close" onClick={() => setShowCertificateModal(false)}>İptal</button>
              <button className="btn-save" onClick={handleAddCertificateFromModal}>Sertifikayı Ekle</button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Modalı */}
      {showAdminModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <div className="admin-header">
              <div className="admin-header-left">
                <button className="admin-sidebar-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                    ☰
                </button>
                <h2 style={{display:'flex', alignItems:'center', gap:'8px'}}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
                  Admin Paneli
                </h2>
              </div>
              <div className="admin-header-right">
                <button className="btn-close-icon" onClick={handleClose}>
                  &times;
                </button>
              </div>
            </div>
            
            <div className="admin-body">
              {/* Mobile Overlay */}
              <div 
                className={`admin-sidebar-overlay ${isSidebarOpen ? 'open' : ''}`} 
                onClick={() => setIsSidebarOpen(false)}
              ></div>

              {/* Sidebar Menü */}
              <div className={`admin-sidebar ${isSidebarOpen ? 'open' : ''}`}>
                {SECTIONS.map(section => (
                  <a 
                    key={section.id} 
                    href={`#admin-section-${section.id}`} 
                    className={`admin-sidebar-link ${activeSection === section.id ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveSection(section.id);
                      document.getElementById(`admin-section-${section.id}`)?.scrollIntoView({ behavior: 'smooth' });
                      setIsSidebarOpen(false);
                    }}
                  >
                    {section.title}
                  </a>
                ))}
                {/* Diğer (Tanımsız) Alanlar İçin Link */}
                {editData && Object.keys(editData).some(k => !SECTIONS.flatMap(s => s.keys).includes(k)) && (
                   <a 
                    href="#admin-section-other" 
                    className={`admin-sidebar-link ${activeSection === 'other' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveSection('other');
                      document.getElementById(`admin-section-other`)?.scrollIntoView({ behavior: 'smooth' });
                      setIsSidebarOpen(false);
                    }}
                  >
                    Diğer 
                  </a>
                )}
              </div>

              {/* İçerik Alanı */}
              <div className="admin-content-area">
                {editData && SECTIONS.map(section => (
                  <div key={section.id} id={`admin-section-${section.id}`} className="admin-section-container">
                    <h3 className="admin-section-title">{section.title}</h3>
                    {section.keys.map(key => renderField(key, editData[key]))}
                  </div>
                ))}
                {/* Diğer (Tanımsız) Alanlar */}
                {editData && Object.keys(editData).filter(k => !SECTIONS.flatMap(s => s.keys).includes(k)).length > 0 && (
                  <div id="admin-section-other" className="admin-section-container">
                    <h3 className="admin-section-title">Diğer</h3>
                    {Object.keys(editData).filter(k => !SECTIONS.flatMap(s => s.keys).includes(k)).map(key => renderField(key, editData[key]))}
                  </div>
                )}
              </div> 
            </div>
            <div className="admin-modal-footer">
              <button className="btn-close" onClick={handleClose}>Kapat</button>
              <button className="btn-undo" onClick={handleUndoChanges}>Geri Al</button>
              <button className="btn-reset" onClick={handleResetContent}>Sıfırla</button>
              <button className="btn-save" onClick={handleSaveContent}>Değişiklikleri Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Sadece Giriş Yapıldığında Görünen Admin Butonları */}
      {isAdmin && (
        <div className="admin-fab-container">
          <button 
            className="admin-fab"
            onClick={openAdminPanel}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
            Admin Paneli
          </button>
          <button 
            className="admin-fab logout-fab"
            onClick={handleLogout}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Çıkış Yap
          </button>
        </div>
      )}

      {/* Mobil ve Touch Ekranlar İçin Gizli Tetikleyici (Sol Alt Köşeye Çift Tıklama) */}
      {!isAdmin && (
        <div 
          onDoubleClick={handleAdminLogin}
          style={{ position: 'fixed', bottom: 0, left: 0, width: '80px', height: '80px', zIndex: 9999 }}
        />
      )}
    </div>
  );
}
